import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  CrsRule,
  SiteWafDirective,
  SiteWafDirectiveAttributes,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import Button from "~/components/mantine/button/Button";
import Select from "~/components/mantine/select/Select";
import AddItemButton from "~/components/ui/addItemButton/AddItemButton";
import Modal from "~/components/ui/modal/Modal";
import { SearchBar } from "~/components/ui/search/SearchBar";
import TogglePill from "~/components/ui/togglePill/TogglePill";
import { tryCatchParsed } from "~/helpers/tryCatch";

import styles from "./WafDirectiveModal.module.scss";
import { CRS_GROUP_INFO } from "./crsGroups";

// ── Types ──────────────────────────────────────────────────────

type ActionType = "log" | "allow" | "deny";
type PathMatchType = "equals" | "prefix" | "glob" | "regex";
type HostMatchType = "equals" | "suffix" | "regex";
type ContentTypeMatchType = "equals" | "prefix" | "regex";
type ValueMatchType = "exists" | "equals" | "contains" | "regex";
type NameKind = "name" | "name_regex";

interface MatchEntry {
  id: string;
  nameKind: NameKind;
  name: string;
  matchType: ValueMatchType;
  value: string;
  existsValue: boolean;
}

interface ConditionsFormState {
  path: { enabled: boolean; matchType: PathMatchType; values: string };
  method: { enabled: boolean; selected: string[] };
  scheme: { enabled: boolean; selected: string[] };
  host: { enabled: boolean; matchType: HostMatchType; values: string };
  content_type: {
    enabled: boolean;
    matchType: ContentTypeMatchType;
    values: string;
  };
  ip: { enabled: boolean; values: string };
  query: { enabled: boolean; matches: MatchEntry[] };
  headers: { enabled: boolean; matches: MatchEntry[] };
  cookies: { enabled: boolean; matches: MatchEntry[] };
}

// Lightweight seed type for opening the modal pre-filled (e.g. from a
// triggered-rule "Add exception" button in the Visibility Engine logs).
// Applies only when `directive === null` (create mode); ignored when
// editing an existing directive.
export type DirectiveSeed = {
  rules?: number[];
  /** HTTP verb to pre-select in the method condition, e.g. "GET". */
  method?: string;
  /** Exact path to pre-fill in the path condition. */
  path?: string;
};

export type DirectiveModalProps = {
  siteId: number;
  directive: SiteWafDirective | null;
  availableRules: CrsRule[];
  availableGroupIds: number[];
  onClose: () => void;
  queryKey: readonly unknown[];
  seed?: DirectiveSeed;
};

// ── Helpers ────────────────────────────────────────────────────

const newEntryId = () => crypto.randomUUID();

function defaultEntry(): MatchEntry {
  return {
    id: newEntryId(),
    nameKind: "name",
    name: "",
    matchType: "exists",
    value: "",
    existsValue: true,
  };
}

// Build initial form conditions from a seed (only path + method are
// supported today — other condition fields stay disabled). Used when
// the modal is opened pre-filled from an external entry point like the
// Visibility Engine "Add exception" button.
function seedConditions(seed: DirectiveSeed | undefined): ConditionsFormState {
  const s = defaultConditions();
  if (!seed) return s;

  if (seed.path) {
    s.path = { enabled: true, matchType: "equals", values: seed.path };
  }
  if (seed.method) {
    s.method = { enabled: true, selected: [seed.method.toUpperCase()] };
  }

  return s;
}

function defaultConditions(): ConditionsFormState {
  return {
    path: { enabled: false, matchType: "prefix", values: "" },
    method: { enabled: false, selected: [] },
    scheme: { enabled: false, selected: [] },
    host: { enabled: false, matchType: "equals", values: "" },
    content_type: { enabled: false, matchType: "prefix", values: "" },
    ip: { enabled: false, values: "" },
    query: { enabled: false, matches: [defaultEntry()] },
    headers: { enabled: false, matches: [defaultEntry()] },
    cookies: { enabled: false, matches: [defaultEntry()] },
  };
}

function parseConditions(
  cond: SiteWafDirective["conditions"],
): ConditionsFormState {
  const s = defaultConditions();

  if (cond.path) {
    const matchType = (Object.keys(cond.path)[0] as PathMatchType) ?? "prefix";
    const vals: string[] =
      (cond.path as Record<string, string[]>)[matchType] ?? [];
    s.path = { enabled: true, matchType, values: vals.join("\n") };
  }

  if (cond.method?.in?.length) {
    s.method = { enabled: true, selected: cond.method.in as string[] };
  }

  if (cond.scheme?.in?.length) {
    s.scheme = { enabled: true, selected: cond.scheme.in as string[] };
  }

  if (cond.host) {
    const matchType = (Object.keys(cond.host)[0] as HostMatchType) ?? "equals";
    const vals: string[] =
      (cond.host as Record<string, string[]>)[matchType] ?? [];
    s.host = { enabled: true, matchType, values: vals.join("\n") };
  }

  if (cond.content_type) {
    const matchType =
      (Object.keys(cond.content_type)[0] as ContentTypeMatchType) ?? "prefix";
    const vals: string[] =
      (cond.content_type as Record<string, string[]>)[matchType] ?? [];
    s.content_type = { enabled: true, matchType, values: vals.join("\n") };
  }

  if (cond.ip?.cidr?.length) {
    s.ip = { enabled: true, values: cond.ip.cidr.join("\n") };
  }

  const parseMatches = (matchList: Record<string, unknown>[]): MatchEntry[] =>
    matchList.map((m) => {
      const nameKind: NameKind = "name_regex" in m ? "name_regex" : "name";
      const rawName = nameKind === "name_regex" ? m.name_regex : m.name;
      const name = Array.isArray(rawName)
        ? rawName.join(", ")
        : String(rawName ?? "");

      let matchType: ValueMatchType = "exists";
      let value = "";
      let existsValue = true;

      if ("exists" in m) {
        matchType = "exists";
        existsValue = Boolean(m.exists);
      } else if ("equals" in m) {
        matchType = "equals";
        const v = m.equals;
        value = Array.isArray(v) ? v.join(", ") : String(v ?? "");
      } else if ("contains" in m) {
        matchType = "contains";
        const v = m.contains;
        value = Array.isArray(v) ? v.join(", ") : String(v ?? "");
      } else if ("regex" in m) {
        matchType = "regex";
        value = String(m.regex ?? "");
      }

      return {
        id: newEntryId(),
        nameKind,
        name,
        matchType,
        value,
        existsValue,
      };
    });

  if (cond.query?.match?.length) {
    s.query = {
      enabled: true,
      matches: parseMatches(cond.query.match as Record<string, unknown>[]),
    };
  }

  const normalizeNameKind = (entries: MatchEntry[]): MatchEntry[] =>
    entries.map((e) =>
      e.nameKind === "name_regex" ? { ...e, nameKind: "name" as NameKind } : e,
    );

  if (cond.headers?.match?.length) {
    s.headers = {
      enabled: true,
      matches: normalizeNameKind(
        parseMatches(cond.headers.match as Record<string, unknown>[]),
      ),
    };
  }

  if (cond.cookies?.match?.length) {
    s.cookies = {
      enabled: true,
      matches: normalizeNameKind(
        parseMatches(cond.cookies.match as Record<string, unknown>[]),
      ),
    };
  }

  return s;
}

function buildConditions(
  s: ConditionsFormState,
): SiteWafDirective["conditions"] {
  const out: Record<string, unknown> = {};

  if (s.path.enabled) {
    const vals = s.path.values
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
    if (vals.length) out.path = { [s.path.matchType]: vals };
  }

  if (s.method.enabled && s.method.selected.length) {
    out.method = { in: s.method.selected };
  }

  if (s.scheme.enabled && s.scheme.selected.length) {
    out.scheme = { in: s.scheme.selected };
  }

  if (s.host.enabled) {
    const vals = s.host.values
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
    if (vals.length) out.host = { [s.host.matchType]: vals };
  }

  if (s.content_type.enabled) {
    const vals = s.content_type.values
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
    if (vals.length) out.content_type = { [s.content_type.matchType]: vals };
  }

  if (s.ip.enabled) {
    const vals = s.ip.values
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
    if (vals.length) out.ip = { cidr: vals };
  }

  const buildMatchList = (matches: MatchEntry[]) =>
    matches
      .filter((m) => m.name.trim())
      .map((m) => {
        const entry: Record<string, unknown> =
          m.nameKind === "name_regex"
            ? { name_regex: m.name.trim() }
            : {
                name: m.name.includes(",")
                  ? m.name
                      .split(",")
                      .map((n) => n.trim())
                      .filter(Boolean)
                  : m.name.trim(),
              };

        if (m.matchType === "exists") {
          entry.exists = m.existsValue;
        } else if (m.value.trim()) {
          if (m.matchType === "equals" || m.matchType === "contains") {
            const parts = m.value
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);
            entry[m.matchType] = parts.length === 1 ? parts[0] : parts;
          } else {
            entry[m.matchType] = m.value.trim();
          }
        }

        return entry;
      })
      .filter((e) => Object.keys(e).length > 1);

  if (s.query.enabled) {
    const list = buildMatchList(s.query.matches);
    if (list.length) out.query = { match: list };
  }

  if (s.headers.enabled) {
    const list = buildMatchList(s.headers.matches);
    if (list.length) out.headers = { match: list };
  }

  if (s.cookies.enabled) {
    const list = buildMatchList(s.cookies.matches);
    if (list.length) out.cookies = { match: list };
  }

  return out as SiteWafDirective["conditions"];
}

// ── Textarea placeholders ──────────────────────────────────────

const PLACEHOLDERS = {
  path: {
    prefix: "/api/v1/\n/admin/",
    equals: "/wp-login.php\n/.env",
    glob: "/assets/**/*.css\n/images/**/*.png",
    regex: "\\.php$\n\\.asp$",
  } satisfies Record<PathMatchType, string>,

  host: {
    equals: "api.example.com\napp.example.com",
    suffix: ".example.com\n.internal.net",
    regex: "^api\\..*\\.com$",
  } satisfies Record<HostMatchType, string>,

  content_type: {
    prefix: "application/json\nmultipart/form-data",
    equals: "application/json\napplication/graphql",
    regex: "application\\/json\napplication\\/.*\\+json",
  } satisfies Record<ContentTypeMatchType, string>,

  ip: "192.168.1.1\n10.0.0.0/8\n203.0.113.0/24",
};

// ── Action class maps ──────────────────────────────────────────

const ACTION_BTN_ACTIVE_CLASS: Record<ActionType, string> = {
  log: "actionBtnActiveLog",
  allow: "actionBtnActiveAllow",
  deny: "actionBtnActiveBlock",
};

// ── Match entry select options ──────────────────────────────────

const NAME_MATCH_OPTIONS = [
  {
    group: "exact name",
    items: [
      { value: "name:exists", label: "exists / absent" },
      { value: "name:equals", label: "equals" },
      { value: "name:contains", label: "contains" },
      { value: "name:regex", label: "regex" },
    ],
  },
];

const NAME_MATCH_OPTIONS_WITH_REGEX = [
  ...NAME_MATCH_OPTIONS,
  {
    group: "regex name",
    items: [
      { value: "name_regex:exists", label: "regex name · exists" },
      { value: "name_regex:equals", label: "regex name · equals" },
      { value: "name_regex:contains", label: "regex name · contains" },
      { value: "name_regex:regex", label: "regex name · regex" },
    ],
  },
];

// ── Sub-components ─────────────────────────────────────────────

const ACTIONS: ActionType[] = ["log", "allow", "deny"];
const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];
const SCHEMES = ["http", "https"];
const PATH_MATCH_TYPES: PathMatchType[] = ["prefix", "equals", "glob", "regex"];
const HOST_MATCH_TYPES: HostMatchType[] = ["equals", "suffix", "regex"];
const CONTENT_TYPE_MATCH_TYPES: ContentTypeMatchType[] = [
  "prefix",
  "equals",
  "regex",
];

function MatchEntryRow({
  entry,
  onChange,
  onRemove,
  supportsNameRegex = false,
}: {
  entry: MatchEntry;
  onChange: (updated: MatchEntry) => void;
  onRemove: () => void;
  supportsNameRegex?: boolean;
}) {
  const up = (patch: Partial<MatchEntry>) => onChange({ ...entry, ...patch });

  return (
    <div className={styles.matchEntry}>
      {/* Name field */}
      <input
        className={styles.matchEntryInput}
        placeholder={
          entry.nameKind === "name"
            ? "name (comma-separate multiple)"
            : "name regex"
        }
        value={entry.name}
        onChange={(e) => up({ name: e.currentTarget.value })}
      />

      {/* Name kind + value match type combined */}
      <Select
        data={
          supportsNameRegex ? NAME_MATCH_OPTIONS_WITH_REGEX : NAME_MATCH_OPTIONS
        }
        value={`${entry.nameKind}:${entry.matchType}`}
        onChange={(v) => {
          if (!v) return;
          const [nk, mt] = v.split(":") as [NameKind, ValueMatchType];
          up({ nameKind: nk, matchType: mt });
        }}
        searchable={false}
        className={styles.matchEntrySelect}
      />

      {/* Value */}
      {entry.matchType === "exists" ? (
        <div className={styles.matchEntryExistsGroup}>
          <button
            type="button"
            className={`${styles.matchEntryExistsBtn} ${entry.existsValue ? styles.present : ""}`}
            onClick={() => up({ existsValue: true })}
          >
            present
          </button>
          <button
            type="button"
            className={`${styles.matchEntryExistsBtn} ${!entry.existsValue ? styles.absent : ""}`}
            onClick={() => up({ existsValue: false })}
          >
            absent
          </button>
        </div>
      ) : (
        <input
          className={styles.matchEntryInput}
          placeholder={
            entry.matchType === "regex"
              ? "regex pattern"
              : "value (comma-separate multiple)"
          }
          value={entry.value}
          onChange={(e) => up({ value: e.currentTarget.value })}
        />
      )}

      <button
        type="button"
        className={styles.matchEntryRemove}
        onClick={onRemove}
        title="Remove"
      >
        ×
      </button>
    </div>
  );
}

function ConditionSection({
  title,
  enabled,
  summary,
  onToggle,
  children,
}: {
  title: string;
  enabled: boolean;
  summary?: string;
  onToggle: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.conditionRow}>
      <button
        type="button"
        className={`${styles.conditionHeader} ${enabled ? styles.active : ""}`}
        onClick={() => onToggle(!enabled)}
      >
        <span className={styles.conditionChevron}>{enabled ? "▼" : "▶"}</span>
        <span className={styles.conditionHeaderTitle}>{title}</span>
        {!enabled && summary && (
          <span className={styles.conditionHeaderSummary}>{summary}</span>
        )}
        <span
          className={`${styles.conditionToggleBadge} ${enabled ? styles.on : ""}`}
        >
          {enabled ? "ON" : "OFF"}
        </span>
      </button>

      {enabled && <div className={styles.conditionBody}>{children}</div>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────

export function WafDirectiveModal({
  siteId,
  directive,
  availableRules,
  availableGroupIds,
  onClose,
  queryKey,
  seed,
}: DirectiveModalProps) {
  const qc = useQueryClient();
  const isEdit = directive !== null;

  const [action, setAction] = useState<ActionType>(directive?.action ?? "log");
  const [selectedRules, setSelectedRules] = useState<number[]>(
    directive?.rules ?? seed?.rules ?? [],
  );
  const [selectedGroups, setSelectedGroups] = useState<number[]>(
    directive?.groups ?? [],
  );
  const [conditions, setConditions] = useState<ConditionsFormState>(() =>
    directive ? parseConditions(directive.conditions) : seedConditions(seed),
  );
  const [openGroupIds, setOpenGroupIds] = useState<Set<number>>(new Set());
  const [closedSearchGroupIds, setClosedSearchGroupIds] = useState<Set<number>>(
    new Set(),
  );
  const [ruleSearch, setRuleSearch] = useState("");

  // Reset form only when the directive identity changes (different id or null→edit
  // transition), not on every parent re-render that produces a new object reference.
  useEffect(() => {
    setAction(directive?.action ?? "log");
    setSelectedRules(directive?.rules ?? seed?.rules ?? []);
    setSelectedGroups(directive?.groups ?? []);
    setConditions(
      directive ? parseConditions(directive.conditions) : seedConditions(seed),
    );
    // The seed object is captured at mount time. We intentionally don't react to
    // its identity changing — modal callers unmount + remount the modal to swap
    // seeds, which re-runs the useState initializers above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directive?.id]);

  // Group available rules by group id
  const rulesByGroup = useMemo(() => {
    const map = new Map<number, CrsRule[]>();
    availableRules.forEach((r) => {
      if (!map.has(r.group)) map.set(r.group, []);
      map.get(r.group)!.push(r);
    });

    return map;
  }, [availableRules]);

  const filteredRulesByGroup = useMemo(() => {
    const query = ruleSearch.trim().toLowerCase();

    if (!query) {
      return Array.from(rulesByGroup.entries());
    }

    return Array.from(rulesByGroup.entries()).flatMap(([gid, rules]) => {
      const info = CRS_GROUP_INFO[gid];
      const groupName = info?.name?.toLowerCase() ?? "";
      const filteredRules = rules.filter((rule) => {
        const ruleId = String(rule.id);
        const message = rule.message?.toLowerCase() ?? "";

        return (
          ruleId.includes(query) ||
          message.includes(query) ||
          groupName.includes(query)
        );
      });

      return filteredRules.length > 0 ? [[gid, filteredRules] as const] : [];
    });
  }, [ruleSearch, rulesByGroup]);

  const normalizeSelection = useCallback(
    (nextRules: number[], nextGroups: number[]) => {
      const normalizedGroups = new Set(nextGroups);
      let normalizedRules = [...nextRules];

      availableGroupIds.forEach((gid) => {
        const groupRuleIds = (rulesByGroup.get(gid) ?? []).map(
          (rule) => rule.id,
        );

        if (groupRuleIds.length === 0) return;

        const hasAllRules = groupRuleIds.every((id) =>
          normalizedRules.includes(id),
        );

        if (hasAllRules) {
          normalizedGroups.add(gid);
          normalizedRules = normalizedRules.filter(
            (id) => !groupRuleIds.includes(id),
          );
        }
      });

      normalizedGroups.forEach((gid) => {
        const groupRuleIds = (rulesByGroup.get(gid) ?? []).map(
          (rule) => rule.id,
        );
        normalizedRules = normalizedRules.filter(
          (id) => !groupRuleIds.includes(id),
        );
      });

      return {
        groups: Array.from(normalizedGroups),
        rules: normalizedRules,
      };
    },
    [availableGroupIds, rulesByGroup],
  );

  const visibleOpenGroupIds = useMemo(() => {
    const query = ruleSearch.trim();

    if (!query) {
      return openGroupIds;
    }

    return new Set(
      filteredRulesByGroup
        .map(([gid]) => gid)
        .filter((gid) => !closedSearchGroupIds.has(gid)),
    );
  }, [closedSearchGroupIds, filteredRulesByGroup, openGroupIds, ruleSearch]);

  useEffect(() => {
    if (!ruleSearch.trim()) {
      setClosedSearchGroupIds(new Set());
    }
  }, [ruleSearch]);

  const toggleGroupOpen = (gid: number) =>
    setOpenGroupIds((prev) => {
      const next = new Set(prev);
      const hasSearch = Boolean(ruleSearch.trim());
      const isMatchedBySearch = filteredRulesByGroup.some(
        ([groupId]) => groupId === gid,
      );

      if (hasSearch && isMatchedBySearch) {
        const isClosed = closedSearchGroupIds.has(gid);

        setClosedSearchGroupIds((closedPrev) => {
          const closedNext = new Set(closedPrev);

          if (isClosed) {
            closedNext.delete(gid);
          } else {
            closedNext.add(gid);
          }

          return closedNext;
        });

        if (isClosed) {
          next.add(gid);
        } else {
          next.delete(gid);
        }

        return next;
      }

      next.has(gid) ? next.delete(gid) : next.add(gid);

      return next;
    });

  const toggleRule = (ruleId: number) => {
    const nextRules = selectedRules.includes(ruleId)
      ? selectedRules.filter((id) => id !== ruleId)
      : [...selectedRules, ruleId];
    const normalized = normalizeSelection(nextRules, selectedGroups);

    setSelectedRules(normalized.rules);
    setSelectedGroups(normalized.groups);
  };

  const toggleRuleInGroup = (gid: number, ruleId: number) => {
    if (selectedGroups.includes(gid)) {
      const nextGroups = selectedGroups.filter((id) => id !== gid);
      const nextRules = selectedRules.filter(
        (id) =>
          (availableRules.find((rule) => rule.id === id)?.group ?? null) !==
          gid,
      );
      const normalized = normalizeSelection([...nextRules, ruleId], nextGroups);

      setSelectedGroups(normalized.groups);
      setSelectedRules(normalized.rules);

      return;
    }

    toggleRule(ruleId);
  };

  const toggleGroup = (gid: number, rules: CrsRule[]) => {
    const ruleIds = rules.map((r) => r.id);
    const nextGroups = selectedGroups.includes(gid)
      ? selectedGroups.filter((id) => id !== gid)
      : [...selectedGroups, gid];
    const nextRules = selectedRules.filter((id) => !ruleIds.includes(id));
    const normalized = normalizeSelection(nextRules, nextGroups);

    setSelectedGroups(normalized.groups);
    setSelectedRules(normalized.rules);
  };

  const patchCondition = useCallback(
    <K extends keyof ConditionsFormState>(
      key: K,
      patch: Partial<ConditionsFormState[K]>,
    ) => {
      setConditions((prev) => ({
        ...prev,
        [key]: { ...prev[key], ...patch },
      }));
    },
    [],
  );

  const patchMatches = (
    key: "query" | "headers" | "cookies",
    updater: (entries: MatchEntry[]) => MatchEntry[],
  ) => {
    setConditions((prev) => {
      const next = updater(prev[key].matches);

      return {
        ...prev,
        [key]:
          next.length > 0
            ? { ...prev[key], matches: next }
            : { enabled: false, matches: [defaultEntry()] },
      };
    });
  };

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { mutateAsync: save, isPending } = useMutation({
    mutationFn: (attrs: SiteWafDirectiveAttributes): Promise<unknown> => {
      if (isEdit) {
        return apiClient.sites.wafDirectivesPartialUpdate(
          siteId,
          directive.id,
          attrs,
        );
      }

      return apiClient.sites.wafDirectivesCreate(siteId, attrs);
    },
  });

  const handleSave = async () => {
    setValidationErrors([]);

    const { data, error } = await tryCatchParsed(
      save({
        action,
        rules: selectedRules,
        groups: selectedGroups,
        conditions: buildConditions(
          conditions,
        ) as SiteWafDirectiveAttributes["conditions"],
      }),
    );

    if (error) {
      if (error.validation_messages?.length) {
        setValidationErrors(error.validation_messages);
      } else {
        enqueueSnackbar(error.message ?? "Failed to save directive.", {
          variant: "error",
          persist: true,
        });
      }

      return;
    }

    void data;
    enqueueSnackbar(isEdit ? "Directive updated." : "Directive created.", {
      variant: "success",
    });
    void qc.invalidateQueries({ queryKey });
    onClose();
  };

  const selectedGroupObjects = useMemo(
    () =>
      selectedGroups.map((gid) => ({
        id: gid,
        name: CRS_GROUP_INFO[gid]?.name ?? `Group ${gid}`,
        rulesCount: rulesByGroup.get(gid)?.length ?? 0,
      })),
    [rulesByGroup, selectedGroups],
  );

  const selectedRuleObjects = useMemo(
    () =>
      availableRules.filter(
        (r) =>
          selectedRules.includes(r.id) && !selectedGroups.includes(r.group),
      ),
    [availableRules, selectedGroups, selectedRules],
  );

  // ── Render ─────────────────────────────────────────────────

  return (
    <Modal
      size="xl"
      isOpen
      closeModal={onClose}
      modalLabel={
        <span className={styles.modalTitle}>
          {isEdit ? `Edit Directive #${directive.id}` : "New WAF Directive"}
        </span>
      }
    >
      <div className={styles.inner}>
        <div className={styles.scroll}>
          {/* ── Action ── */}
          <div>
            <p className={styles.sectionLabel}>Action</p>
            <div className={styles.actionGroup}>
              {ACTIONS.map((a) => (
                <button
                  key={a}
                  type="button"
                  className={
                    action === a
                      ? styles[
                          ACTION_BTN_ACTIVE_CLASS[a] as keyof typeof styles
                        ]
                      : styles.actionBtn
                  }
                  onClick={() => setAction(a)}
                >
                  {a.charAt(0).toUpperCase() + a.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {availableGroupIds.length > 0 && (
            <div>
              <p className={styles.sectionLabel}>
                Apply to Rules and Groups{" "}
                <span className={styles.sectionLabelNote}>
                  — select a whole group or pick individual rule IDs
                </span>
              </p>

              <div className={styles.rulesToolbar}>
                <div className={styles.chips}>
                  {selectedGroupObjects.length === 0 &&
                  selectedRuleObjects.length === 0 ? (
                    <span className={styles.emptyChips}>
                      No groups or rules selected
                    </span>
                  ) : (
                    <>
                      {selectedGroupObjects.map((group) => (
                        <span key={`group-${group.id}`} className={styles.chip}>
                          <span>
                            {group.name} · all {group.rulesCount} rules
                          </span>
                          <button
                            type="button"
                            className={styles.chipRemove}
                            onClick={() =>
                              toggleGroup(
                                group.id,
                                rulesByGroup.get(group.id) ?? [],
                              )
                            }
                            title="Remove group"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {selectedRuleObjects.map((r) => (
                        <span key={r.id} className={styles.chip}>
                          <span>{r.id}</span>
                          <button
                            type="button"
                            className={styles.chipRemove}
                            onClick={() => toggleRule(r.id)}
                            title="Remove rule"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </>
                  )}
                </div>
                <div className={styles.rulesSearch}>
                  <SearchBar
                    value={ruleSearch}
                    onChange={setRuleSearch}
                    placeholder="Find rule ID or text"
                  />
                </div>
              </div>

              <div
                className={`${styles.rulesAccordion} ${styles.rulesAccordionWithGap}`}
              >
                {filteredRulesByGroup.map(([gid, rules]) => {
                  const info = CRS_GROUP_INFO[gid];
                  const isOpen = visibleOpenGroupIds.has(gid);
                  const allGroupRules = rulesByGroup.get(gid) ?? rules;
                  const isGroupSelected = selectedGroups.includes(gid);
                  const checkedCount = isGroupSelected
                    ? allGroupRules.length
                    : allGroupRules.filter((r) => selectedRules.includes(r.id))
                        .length;
                  const isFullySelected =
                    checkedCount === allGroupRules.length &&
                    allGroupRules.length > 0;
                  const isPartiallySelected =
                    checkedCount > 0 && checkedCount < allGroupRules.length;

                  return (
                    <div key={gid}>
                      <div
                        role="button"
                        tabIndex={0}
                        className={
                          isOpen
                            ? styles.rulesGroupHeaderOpen
                            : styles.rulesGroupHeader
                        }
                        onClick={() => toggleGroupOpen(gid)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && toggleGroupOpen(gid)
                        }
                      >
                        <span
                          className={`${styles.rulesGroupChevron} ${isOpen ? styles.open : ""}`}
                        >
                          ▼
                        </span>
                        <div className={styles.rulesGroupMeta}>
                          <span className={styles.rulesGroupNum}>{gid}</span>
                          <span className={styles.rulesGroupName}>
                            {info?.name ?? `Group ${gid}`}
                          </span>
                        </div>
                        <div className={styles.rulesGroupActions}>
                          {isFullySelected && (
                            <span className={styles.rulesGroupChecked}>
                              selected all {allGroupRules.length} rules
                            </span>
                          )}
                          {isPartiallySelected && (
                            <span className={styles.rulesGroupChecked}>
                              selected {checkedCount} of {allGroupRules.length}{" "}
                              rules
                            </span>
                          )}
                          {!isGroupSelected && !isPartiallySelected && (
                            <span className={styles.rulesGroupSummary}>
                              {allGroupRules.length} rules
                            </span>
                          )}
                          <button
                            type="button"
                            className={styles.rulesGroupToggleAllBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleGroup(gid, allGroupRules);
                            }}
                          >
                            {isGroupSelected ? "deselect all" : "select all"}
                          </button>
                        </div>
                      </div>

                      {isOpen && (
                        <div className={styles.rulesList}>
                          {rules.map((rule) => (
                            <label key={rule.id} className={styles.ruleItem}>
                              <input
                                type="checkbox"
                                checked={
                                  isGroupSelected ||
                                  selectedRules.includes(rule.id)
                                }
                                onChange={() => toggleRuleInGroup(gid, rule.id)}
                              />
                              <div className={styles.ruleItemLabel}>
                                <span className={styles.ruleId}>{rule.id}</span>
                                {rule.message && (
                                  <span className={styles.ruleMessage}>
                                    {rule.message}
                                  </span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {filteredRulesByGroup.length === 0 && (
                  <div className={styles.rulesEmptyState}>
                    No rules match "{ruleSearch.trim()}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Conditions ── */}
          <div>
            <p className={styles.sectionLabel}>
              Request Conditions{" "}
              <span className={styles.sectionLabelNote}>
                — all enabled conditions must match (AND logic). Leave all off
                to match every request.
              </span>
            </p>

            <div className={styles.conditionsList}>
              {/* Path */}
              <ConditionSection
                title="Path"
                enabled={conditions.path.enabled}
                summary={conditions.path.values.split("\n")[0] || undefined}
                onToggle={(v) => patchCondition("path", { enabled: v })}
              >
                <div className={styles.matchTypeGroup}>
                  {PATH_MATCH_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`${styles.matchTypeBtn} ${conditions.path.matchType === t ? styles.active : ""}`}
                      onClick={() => patchCondition("path", { matchType: t })}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <textarea
                  className={styles.textarea}
                  placeholder={PLACEHOLDERS.path[conditions.path.matchType]}
                  value={conditions.path.values}
                  onChange={(e) =>
                    patchCondition("path", { values: e.currentTarget.value })
                  }
                />
                <span className={styles.hint}>
                  One value per line. Paths must start with /
                </span>
              </ConditionSection>

              {/* Method */}
              <ConditionSection
                title="HTTP Method"
                enabled={conditions.method.enabled}
                summary={conditions.method.selected.join(", ") || undefined}
                onToggle={(v) => patchCondition("method", { enabled: v })}
              >
                <div className={styles.checkGrid}>
                  {METHODS.map((m) => (
                    <TogglePill
                      key={m}
                      label={m}
                      checked={conditions.method.selected.includes(m)}
                      onChange={() =>
                        patchCondition("method", {
                          selected: conditions.method.selected.includes(m)
                            ? conditions.method.selected.filter((x) => x !== m)
                            : [...conditions.method.selected, m],
                        })
                      }
                    />
                  ))}
                </div>
              </ConditionSection>

              {/* Scheme */}
              <ConditionSection
                title="Scheme"
                enabled={conditions.scheme.enabled}
                summary={conditions.scheme.selected.join(", ") || undefined}
                onToggle={(v) => patchCondition("scheme", { enabled: v })}
              >
                <div className={styles.checkGrid}>
                  {SCHEMES.map((s) => (
                    <TogglePill
                      key={s}
                      label={s}
                      checked={conditions.scheme.selected.includes(s)}
                      onChange={() =>
                        patchCondition("scheme", {
                          selected: conditions.scheme.selected.includes(s)
                            ? conditions.scheme.selected.filter((x) => x !== s)
                            : [...conditions.scheme.selected, s],
                        })
                      }
                    />
                  ))}
                </div>
              </ConditionSection>

              {/* Host */}
              <ConditionSection
                title="Host"
                enabled={conditions.host.enabled}
                onToggle={(v) => patchCondition("host", { enabled: v })}
              >
                <div className={styles.matchTypeGroup}>
                  {HOST_MATCH_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`${styles.matchTypeBtn} ${conditions.host.matchType === t ? styles.active : ""}`}
                      onClick={() => patchCondition("host", { matchType: t })}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <textarea
                  className={styles.textarea}
                  placeholder={PLACEHOLDERS.host[conditions.host.matchType]}
                  value={conditions.host.values}
                  onChange={(e) =>
                    patchCondition("host", { values: e.currentTarget.value })
                  }
                />
                <span className={styles.hint}>One host per line.</span>
              </ConditionSection>

              {/* Content-Type */}
              <ConditionSection
                title="Content-Type"
                enabled={conditions.content_type.enabled}
                onToggle={(v) => patchCondition("content_type", { enabled: v })}
              >
                <div className={styles.matchTypeGroup}>
                  {CONTENT_TYPE_MATCH_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`${styles.matchTypeBtn} ${conditions.content_type.matchType === t ? styles.active : ""}`}
                      onClick={() =>
                        patchCondition("content_type", { matchType: t })
                      }
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <textarea
                  className={styles.textarea}
                  placeholder={
                    PLACEHOLDERS.content_type[conditions.content_type.matchType]
                  }
                  value={conditions.content_type.values}
                  onChange={(e) =>
                    patchCondition("content_type", {
                      values: e.currentTarget.value,
                    })
                  }
                />
                <span className={styles.hint}>One content-type per line.</span>
              </ConditionSection>

              {/* IP */}
              <ConditionSection
                title="IP / CIDR"
                enabled={conditions.ip.enabled}
                onToggle={(v) => patchCondition("ip", { enabled: v })}
              >
                <textarea
                  className={styles.textarea}
                  placeholder={PLACEHOLDERS.ip}
                  value={conditions.ip.values}
                  onChange={(e) =>
                    patchCondition("ip", { values: e.currentTarget.value })
                  }
                />
                <span className={styles.hint}>
                  One IP/CIDR range per line. Supports IPv4 only.
                </span>
              </ConditionSection>

              {/* Query */}
              <ConditionSection
                title="Query Parameters"
                enabled={conditions.query.enabled}
                onToggle={(v) => patchCondition("query", { enabled: v })}
              >
                <div className={styles.matchEntries}>
                  {conditions.query.matches.map((entry, idx) => (
                    <MatchEntryRow
                      key={entry.id}
                      supportsNameRegex
                      entry={entry}
                      onChange={(updated) =>
                        patchMatches("query", (prev) =>
                          prev.map((e) => (e.id === entry.id ? updated : e)),
                        )
                      }
                      onRemove={() =>
                        patchMatches("query", (prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                    />
                  ))}
                </div>
                <AddItemButton
                  className={styles.addMatchBtn}
                  onClick={() =>
                    patchMatches("query", (prev) => [...prev, defaultEntry()])
                  }
                  disabled={conditions.query.matches.length >= 5}
                >
                  + Add parameter match
                </AddItemButton>
              </ConditionSection>

              {/* Headers */}
              <ConditionSection
                title="Request Headers"
                enabled={conditions.headers.enabled}
                onToggle={(v) => patchCondition("headers", { enabled: v })}
              >
                <div className={styles.matchEntries}>
                  {conditions.headers.matches.map((entry, idx) => (
                    <MatchEntryRow
                      key={entry.id}
                      entry={entry}
                      onChange={(updated) =>
                        patchMatches("headers", (prev) =>
                          prev.map((e) => (e.id === entry.id ? updated : e)),
                        )
                      }
                      onRemove={() =>
                        patchMatches("headers", (prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                    />
                  ))}
                </div>
                <AddItemButton
                  className={styles.addMatchBtn}
                  onClick={() =>
                    patchMatches("headers", (prev) => [...prev, defaultEntry()])
                  }
                  disabled={conditions.headers.matches.length >= 5}
                >
                  + Add header match
                </AddItemButton>
              </ConditionSection>

              {/* Cookies */}
              <ConditionSection
                title="Cookies"
                enabled={conditions.cookies.enabled}
                onToggle={(v) => patchCondition("cookies", { enabled: v })}
              >
                <div className={styles.matchEntries}>
                  {conditions.cookies.matches.map((entry, idx) => (
                    <MatchEntryRow
                      key={entry.id}
                      entry={entry}
                      onChange={(updated) =>
                        patchMatches("cookies", (prev) =>
                          prev.map((e) => (e.id === entry.id ? updated : e)),
                        )
                      }
                      onRemove={() =>
                        patchMatches("cookies", (prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                    />
                  ))}
                </div>
                <AddItemButton
                  className={styles.addMatchBtn}
                  onClick={() =>
                    patchMatches("cookies", (prev) => [...prev, defaultEntry()])
                  }
                  disabled={conditions.cookies.matches.length >= 5}
                >
                  + Add cookie match
                </AddItemButton>
              </ConditionSection>
            </div>
          </div>
        </div>

        {/* ── Validation errors ── */}
        {validationErrors.length > 0 && (
          <div className={styles.validationErrors}>
            <span className={styles.validationErrorsIcon}>⚠</span>
            <ul className={styles.validationErrorsList}>
              {validationErrors.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <Button variant="outlined" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Saving…" : isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
